import { useEffect, useState } from "react";
import { Plus, Loader2, Shield, Trash2, Pencil } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const PERM_GROUPS = {
  "User Management": ["users.view", "users.manage"],
  "Roles & Permissions": ["roles.view", "roles.manage"],
  "Audit Logs": ["audit.view"],
  "Master Data": ["master_data.view", "master_data.manage"],
  "Vessels": ["vessels.view", "vessels.manage"],
  "Crew Requirements": ["requirements.view", "requirements.manage"],
  "Documents": ["documents.view", "documents.manage"],
  "Contracts (SEA)": ["contracts.view", "contracts.manage"],
  "Travel": ["travel.view", "travel.manage"],
  "Reports": ["reports.view"],
};

function permLabel(p) {
  const [, action] = p.split(".");
  return action.charAt(0).toUpperCase() + action.slice(1);
}

export default function RolesPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("roles.manage");
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/roles");
      setRoles(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (r) => {
    if (!window.confirm(`Delete role “${r.role_name}”?`)) return;
    try {
      await api.delete(`/roles/${r.id}`);
      toast.success("Role deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="roles-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">Access control</div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight mt-1" style={{ fontFamily: "Manrope" }}>
            Roles & Permissions
          </h1>
          <div className="text-sm text-slate-500 mt-1">Create unlimited custom roles with precise permissions.</div>
        </div>

        {canManage && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0b192c] hover:bg-[#0f2544] h-10" data-testid="role-create-btn">
                <Plus className="w-4 h-4 mr-1.5" /> New role
              </Button>
            </DialogTrigger>
            <RoleFormDialog
              onDone={() => { setCreateOpen(false); load(); }}
              mode="create"
            />
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {loading && <div className="col-span-full text-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400 inline" /></div>}

        {!loading && roles.map((r) => (
          <div key={r.id} className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-9 h-9 rounded-md bg-cyan-50 text-cyan-700 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 break-words">{r.role_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{r.domain || "—"} • {r.permissions_json.length} permissions</div>
                  {!r.is_expandable && (
                    <span className="chip chip-warning mt-2">protected</span>
                  )}
                </div>
              </div>
              {canManage && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(r)} data-testid={`edit-role-${r.role_name}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  {r.is_expandable && (
                    <Button size="sm" variant="ghost" onClick={() => remove(r)} className="text-rose-600 hover:text-rose-700" data-testid={`delete-role-${r.role_name}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              )}
            </div>

            <div className="mt-4 flex flex-wrap gap-1">
              {r.permissions_json.slice(0, 10).map((p) => (
                <span key={p} className="chip chip-info font-mono text-[10px]">{p}</span>
              ))}
              {r.permissions_json.length > 10 && (
                <span className="chip chip-info font-mono text-[10px]">+{r.permissions_json.length - 10} more</span>
              )}
              {r.permissions_json.length === 0 && (
                <span className="text-xs text-slate-400">No permissions assigned</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <RoleFormDialog
          open
          onOpenChange={(o) => !o && setEditing(null)}
          onDone={() => { setEditing(null); load(); }}
          mode="edit"
          role={editing}
        />
      )}
    </div>
  );
}

function RoleFormDialog({ open, onOpenChange, onDone, mode, role }) {
  const [name, setName] = useState(role?.role_name || "");
  const [domain, setDomain] = useState(role?.domain || "");
  const [perms, setPerms] = useState(() => new Set(role?.permissions_json || []));
  const [saving, setSaving] = useState(false);

  const toggle = (p) => {
    const next = new Set(perms);
    if (next.has(p)) next.delete(p); else next.add(p);
    setPerms(next);
  };

  const toggleGroup = (group) => {
    const next = new Set(perms);
    const all = PERM_GROUPS[group];
    const allOn = all.every((p) => next.has(p));
    if (allOn) all.forEach((p) => next.delete(p));
    else all.forEach((p) => next.add(p));
    setPerms(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { role_name: name, domain, permissions_json: Array.from(perms) };
      if (mode === "create") {
        await api.post("/roles", body);
        toast.success("Role created");
      } else {
        await api.patch(`/roles/${role.id}`, body);
        toast.success("Role updated");
      }
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const Body = (
    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="role-form-dialog">
      <DialogHeader>
        <DialogTitle>{mode === "create" ? "Create role" : `Edit role: ${role.role_name}`}</DialogTitle>
        <DialogDescription>Grant precise access to each module.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Role name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required disabled={role?.role_name === "Super Admin"} data-testid="role-name-input" />
          </div>
          <div>
            <Label>Domain / category</Label>
            <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="e.g. Sourcing, Operations" data-testid="role-domain-input" />
          </div>
        </div>

        <div className="space-y-3">
          <Label className="text-xs uppercase tracking-wider text-slate-600">Permissions</Label>
          <div className="divide-y divide-slate-100 border border-slate-200 rounded-md">
            {Object.entries(PERM_GROUPS).map(([group, keys]) => {
              const allOn = keys.every((p) => perms.has(p));
              const someOn = keys.some((p) => perms.has(p));
              return (
                <div key={group} className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-sm text-slate-800">{group}</div>
                    <button
                      type="button"
                      onClick={() => toggleGroup(group)}
                      className="text-xs text-cyan-700 font-medium hover:underline"
                    >
                      {allOn ? "Clear all" : someOn ? "Select all" : "Select all"}
                    </button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2">
                    {keys.map((p) => (
                      <label key={p} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox
                          checked={perms.has(p)}
                          onCheckedChange={() => toggle(p)}
                          disabled={role?.role_name === "Super Admin"}
                          data-testid={`role-permission-checkbox-${p}`}
                        />
                        <span className="text-slate-700">{permLabel(p)}</span>
                        <span className="font-mono text-[10px] text-slate-400">{p}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <DialogFooter>
          <Button type="submit" disabled={saving} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="role-form-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "create" ? "Create role" : "Save changes"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );

  if (mode === "edit") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        {Body}
      </Dialog>
    );
  }
  return Body;
}
